/* eslint-disable @typescript-eslint/member-ordering */
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, HostBinding, ElementRef, HostListener } from '@angular/core';
import { IProvider } from '@app/models/provider';
import { OrchestratorService } from '@app/services/orchestrator.service';
import { firstValueFrom, map, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'mv-current-vets',
	templateUrl: './current-vets.component.html',
	styleUrls: ['./current-vets.component.scss'],
})
export class CurrentVetsComponent implements OnInit, OnDestroy {
	public displayedColumns: string[] = ['name', 'cityState', 'action'];
	public isMobileView: boolean = false;
	public providersList: IProvider[] = [];
	private _petId!: string;
	private _unknownProviderCrmId: string = '7DD4892F-CFEE-DC11-BE3C-001372F7F86F';
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	@HostBinding('attr.data-emb-element') klassName = true;
	@HostListener('window:emb_mv:global_update')
	@HostListener('emb_mv_update_current_vets')
	handleUpdateEvent(): void {
		this.populateList();
	}
	@Input()
	get petId(): string {
		return this._petId;
	}
	set petId(value: string) {
		this._petId = value;
		this.populateList();
	}
	@Output() emb_mv_add_vet: EventEmitter<string> = new EventEmitter<string>();
	@Output() emb_mv_edit_vet: EventEmitter<{ petId: string; clinicId: string }> = new EventEmitter();

	constructor(
		private readonly el: ElementRef<HTMLElement>,
		private readonly breakpointObserver: BreakpointObserver,
		private readonly orchestratorService: OrchestratorService
	) {
		this.breakpointObserver
			.observe(['(min-width: 767px)'])
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(match => {
				this.displayedColumns = match.matches ? ['name', 'cityState', 'action'] : ['name', 'action'];
				this.isMobileView = !match.matches;
			});
	}

	async ngOnInit(): Promise<void> {
		await this.populateList();
	}

	public handleAdd(): void {
		const addVetEvent: CustomEvent = new CustomEvent('emb_mv:global_add_vet', {
			bubbles: true,
			detail: this.petId,
		});
		this.emb_mv_add_vet.emit(this.petId);
		this.el.nativeElement.dispatchEvent(addVetEvent);
	}

	public handleEdit(vet: IProvider): void {
		const editVetEvent: CustomEvent<{ petId: string; clinicId: string }> = new CustomEvent('emb_mv:global_edit_vet', {
			bubbles: true,
			detail: {
				petId: this.petId,
				clinicId: vet.id,
			},
		});
		this.emb_mv_edit_vet.emit({ petId: this.petId, clinicId: vet.id });
		this.el.nativeElement.dispatchEvent(editVetEvent);
	}

	public async populateList(petId: string = this.petId): Promise<void> {
		if (!!petId) {
			this.providersList = await firstValueFrom(
				this.orchestratorService
					.getPetDetails(petId)
					.pipe(map(response => response.pet.providers.filter(clinic => clinic.id.toLowerCase() !== this._unknownProviderCrmId.toLowerCase()) ?? []))
			);
		}
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
